/*
 * 50-50 Text with Image gallery interactions — scoped per
 * [data-slider][data-slider-type="50-50-gallery"] root.
 * Hooks: [data-slider], [data-slider-track], [data-slider-prev/next],
 *        [data-media-card], [data-video-toggle].
 */

// Must stay in sync with Tailwind's lg: breakpoint
const style = getComputedStyle(document.documentElement);
const DESKTOP_BREAKPOINT = parseInt(style.getPropertyValue('--breakpoint-lg'), 10) || 1024;

class ImageWithTextGallery {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-slider-track]');
    this.prevBtn = root.querySelector('[data-slider-prev]');
    this.nextBtn = root.querySelector('[data-slider-next]');
    this.slideCount = parseInt(root.dataset.slideCount, 10) || 0;

    this.currentIndex = 0;
    this.itemsVisible = 1;

    if (this.slideCount === 0) return;

    this.init();
  }

  init() {
    this.updateVisibility();
    this.initVideoControls();
    this.initViewportPause();
    this.bindEvents();
    this.updateState();
  }

  initVideoControls() {
    this.root.querySelectorAll('[data-video-toggle]').forEach((btn) => {
      const video = btn.closest('[data-media-card]')?.querySelector('video');
      if (!video) return;

      video.controls = false;
      const update = () => this.setVideoState(btn, video);

      video.addEventListener('play', () => {
        if (video.dataset.inViewport === 'false' && video.dataset.userPaused !== 'true') {
          video.dataset.autoPaused = 'true';
          video.pause();
          return;
        }
        update();
      });

      video.addEventListener('pause', update);

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        if (video.paused) {
          delete video.dataset.userPaused;
          delete video.dataset.autoPaused;
          video.play().catch(() => {});
        } else {
          video.dataset.userPaused = 'true';
          delete video.dataset.autoPaused;
          video.pause();
        }
      });

      // Seed optimistic: assume the video will autoplay, so the discrete-mode
      // button stays hidden. Only flip to paused if autoplay is genuinely blocked.
      btn.dataset.videoState = 'playing';
      btn.setAttribute('aria-label', 'Pause video');
      btn.setAttribute('aria-pressed', 'true');

      const playProbe = video.play();
      if (playProbe && typeof playProbe.then === 'function') {
        // An AbortError is our own pause() interrupting the probe; only a refusal means paused.
        playProbe.catch((err) => {
          if (err && err.name === 'AbortError') return;
          this.setVideoState(btn, video, { force: true });
        });
      }
    });
  }

  initViewportPause() {
    const videos = this.root.querySelectorAll('video');
    if (!videos.length) return;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      videos.forEach((video) => {
        video.dataset.userPaused = 'true';
        video.pause();
        const btn = video.closest('[data-media-card]')?.querySelector('[data-video-toggle]');
        if (btn) this.setVideoState(btn, video, { force: true });
      });
      return;
    }

    if (!('IntersectionObserver' in window)) return;

    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          video.dataset.inViewport = entry.isIntersecting ? 'true' : 'false';

          if (entry.isIntersecting) {
            if (video.dataset.autoPaused === 'true' && video.dataset.userPaused !== 'true') {
              delete video.dataset.autoPaused;
              video.play().catch(() => {
                video.dataset.autoPaused = 'true';
              });
            }
            return;
          }

          if (!video.paused && video.dataset.userPaused !== 'true') {
            video.dataset.autoPaused = 'true';
            video.pause();
          }
        });
      },
      { threshold: 0.1 },
    );

    videos.forEach((video) => this.viewportObserver.observe(video));
  }

  setVideoState(btn, video, options) {
    if (!btn || !video) return;
    // A pause before playback has begun is start-up noise from our own viewport/breakpoint handling,
    // not a user action. `force` marks the cases that must still show the button.
    if (!(options && options.force) && video.paused && video.played.length === 0) return;
    const isPaused = video.paused;
    btn.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
    btn.setAttribute('aria-pressed', String(!isPaused));
    btn.dataset.videoState = isPaused ? 'paused' : 'playing';
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    let scrollTimer;
    this.track?.addEventListener(
      'scroll',
      () => {
        if (window.innerWidth >= DESKTOP_BREAKPOINT) return;

        if (scrollTimer) cancelAnimationFrame(scrollTimer);
        scrollTimer = requestAnimationFrame(() => {
          const index = Math.round(this.track.scrollLeft / this.track.offsetWidth);
          if (index !== this.currentIndex) {
            this.currentIndex = index;
            this.updateState();
          }
        });
      },
      { passive: true },
    );

    let resizeTimer;
    let windowWidth = window.innerWidth;
    window.addEventListener(
      'resize',
      () => {
        // Prevent iOS vertical scroll from triggering resize logic (address bar hide/show)
        if (window.innerWidth === windowWidth) return;
        windowWidth = window.innerWidth;

        if (resizeTimer) cancelAnimationFrame(resizeTimer);
        resizeTimer = requestAnimationFrame(() => {
          this.updateVisibility();
          this.goTo(this.currentIndex);
          this.updateState();
        });
      },
      { passive: true },
    );
  }

  updateVisibility() {
    this.itemsVisible = window.innerWidth >= DESKTOP_BREAKPOINT ? 2 : 1;
  }

  goTo(index) {
    const cards = this.track?.querySelectorAll('[data-media-card]');
    if (!cards?.length) return;

    this.currentIndex = Math.max(0, Math.min(index, this.slideCount - this.itemsVisible));
    const offset = cards[this.currentIndex].offsetLeft;

    if (window.innerWidth >= DESKTOP_BREAKPOINT) {
      this.track.style.transform = `translateX(-${offset}px)`;
    } else {
      this.track.style.transform = '';
      this.track.scrollLeft = offset;
    }

    this.updateState();
  }

  updateState() {
    this.setButtonState(this.prevBtn, this.currentIndex === 0);
    this.setButtonState(this.nextBtn, this.currentIndex >= this.slideCount - this.itemsVisible);
  }

  setButtonState(btn, isDisabled) {
    if (!btn) return;
    btn.disabled = isDisabled;
    if (isDisabled) {
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-disabled');
    }
  }
}

function initSections(container = document) {
  const selector = '[data-slider][data-slider-type="50-50-gallery"]:not([data-slider-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);

  roots.forEach((root) => {
    root.dataset.sliderInit = 'true';
    new ImageWithTextGallery(root);
  });
}

initSections();

document.addEventListener('shopify:section:load', (event) => initSections(event.target));
